import React, { useEffect, useMemo, useState } from 'react';
import { programService, testimonialAdminService } from '../services/api';
import { Program } from '../types';

const parseList = (value: string) =>
  value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const formatDisplayName = (name?: string) => {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || '';
  return lastInitial ? `${first} ${lastInitial}.` : first;
};

const TestimonialsManagement: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);

  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [programId, setProgramId] = useState<number | ''>('');
  const [subject, setSubject] = useState('Share your study abroad testimonial');
  const [message, setMessage] = useState('We would love to feature your experience. Please share your testimonial using the secure link below.');
  const [emailsText, setEmailsText] = useState('');
  const [phonesText, setPhonesText] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [activeSubmission, setActiveSubmission] = useState<any | null>(null);

  useEffect(() => {
    let mounted = true;
    setProgramsLoading(true);
    programService
      .getAllPrograms()
      .then((data) => {
        if (!mounted) return;
        setPrograms(data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setPrograms([]);
      })
      .finally(() => {
        if (!mounted) return;
        setProgramsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const loadSubmissions = () => {
    setSubmissionsLoading(true);
    setSubmissionsError(null);
    testimonialAdminService
      .listSubmissions()
      .then((data) => setSubmissions(data || []))
      .catch(() => setSubmissionsError('Unable to load submissions.'))
      .finally(() => setSubmissionsLoading(false));
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  const recipients = useMemo(() => {
    if (channel === 'email') {
      return parseList(emailsText).map((email) => ({ email }));
    }
    return parseList(phonesText).map((phone) => ({ phone }));
  }, [channel, emailsText, phonesText]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    setSendError(null);
    setSendSuccess(null);

    if (recipients.length === 0) {
      setSendError(`Add at least one ${channel === 'email' ? 'email address' : 'phone number'}.`);
      return;
    }

    setSendLoading(true);
    try {
      const payload = {
        recipients,
        program_id: programId ? Number(programId) : undefined,
        channel,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      };
      const response = await testimonialAdminService.sendRequests(payload);
      setSendSuccess(`Sent ${response?.length || recipients.length} request(s).`);
      if (channel === 'email') setEmailsText('');
      if (channel === 'sms') setPhonesText('');
    } catch (error) {
      setSendError('Unable to send requests.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleApprove = async (submissionId: number) => {
    try {
      await testimonialAdminService.approveSubmission(submissionId);
      setSubmissions((prev) =>
        prev.map((item) => (item.id === submissionId ? { ...item, status: 'APPROVED' } : item))
      );
    } catch (error) {
      setSubmissionsError('Unable to approve submission.');
    }
  };

  const handleDelete = async (submissionId: number) => {
    const confirmed = window.confirm('Delete this testimonial? This cannot be undone.');
    if (!confirmed) return;
    try {
      await testimonialAdminService.deleteSubmission(submissionId);
      setSubmissions((prev) => prev.filter((item) => item.id !== submissionId));
    } catch (error) {
      setSubmissionsError('Unable to delete submission.');
    }
  };

  return (
    <div className="p-6 space-y-6 text-slate-900 dark:text-slate-100">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 dark:text-emerald-300">Testimonials</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Testimonial Campaigns</h1>
        <p className="text-sm text-slate-500 dark:text-slate-300">Send alumni a secure form link and review submissions.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Send a Campaign</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Requests generate unique links for each recipient.</p>

          <form onSubmit={handleSend} className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Program (optional)</label>
              <select
                value={programId}
                onChange={(event) => setProgramId(event.target.value ? Number(event.target.value) : '')}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                <option value="">All programs</option>
                {programsLoading ? (
                  <option>Loading...</option>
                ) : (
                  programs.map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.title}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Channel</label>
              <select
                value={channel}
                onChange={(event) => setChannel(event.target.value as 'email' | 'sms')}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Message</label>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={4}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">A secure link is appended automatically.</p>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                {channel === 'email' ? 'Recipient emails' : 'Recipient phone numbers'}
              </label>
              <textarea
                value={channel === 'email' ? emailsText : phonesText}
                onChange={(event) =>
                  channel === 'email' ? setEmailsText(event.target.value) : setPhonesText(event.target.value)
                }
                rows={4}
                placeholder={channel === 'email' ? 'alumni1@email.com, alumni2@email.com' : '+1 555 555 1234'}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">Separate entries with commas or new lines.</p>
            </div>

            {sendError ? <div className="text-xs text-rose-500">{sendError}</div> : null}
            {sendSuccess ? <div className="text-xs text-emerald-600 dark:text-emerald-300">{sendSuccess}</div> : null}

            <button
              type="submit"
              disabled={sendLoading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
            >
              {sendLoading ? 'Sending...' : 'Send requests'}
            </button>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Recent Submissions</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Approve to publish in Stage 2.</p>
            </div>
            <button
              type="button"
              onClick={loadSubmissions}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-200"
            >
              Refresh
            </button>
          </div>

          <div className="mt-4">
            {submissionsLoading ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">Loading submissions...</div>
            ) : submissionsError ? (
              <div className="text-xs text-rose-500">{submissionsError}</div>
            ) : submissions.length === 0 ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">No submissions yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-400 dark:text-slate-500">
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Role</th>
                      <th className="py-2 pr-3">Quote</th>
                      <th className="py-2 pr-3">Countries</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Submitted</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr key={submission.id} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-2 pr-3 text-slate-800 dark:text-slate-200">{formatDisplayName(submission.name)}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{submission.role_title || '—'}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{submission.quote || '—'}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">
                          {submission.countries?.length ? submission.countries.join(', ') : '—'}
                        </td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{submission.status || '—'}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{formatDate(submission.created_at)}</td>
                        <td className="py-2">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setActiveSubmission(submission)}
                              className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:border-slate-300 dark:border-slate-700 dark:text-slate-200 dark:hover:text-white dark:hover:border-slate-600"
                            >
                              View
                            </button>
                            {submission.status === 'APPROVED' ? (
                              <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-200">
                                Approved
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleApprove(submission.id)}
                                className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                              >
                                Approve
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(submission.id)}
                              className="inline-flex items-center rounded-full border border-rose-200/70 bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-800/60 dark:bg-rose-900/30 dark:text-rose-200 dark:hover:bg-rose-900/50"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {activeSubmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
            onClick={() => setActiveSubmission(null)}
          />
          <div className="relative max-w-2xl w-full rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 dark:text-emerald-300">Testimonial</p>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{formatDisplayName(activeSubmission.name)}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">{activeSubmission.role_title || 'Alumni'}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubmission(null)}
                className="text-slate-400 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-100"
              >
                ✕
              </button>
            </div>

            {activeSubmission.photo_url ? (
              <img
                src={activeSubmission.photo_url}
                alt={activeSubmission.name || 'Testimonial'}
                className="mt-4 h-28 w-28 rounded-2xl object-cover border border-slate-200 dark:border-slate-700"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            ) : null}

            <div className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
              {activeSubmission.quote && (
                <p className="text-base text-slate-800 dark:text-slate-100">“{activeSubmission.quote}”</p>
              )}
              {activeSubmission.story && <p>{activeSubmission.story}</p>}
              {activeSubmission.countries?.length ? (
                <div className="flex flex-wrap gap-2">
                  {activeSubmission.countries.map((country: string) => (
                    <span
                      key={country}
                      className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-100"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              ) : null}
              {activeSubmission.current_location && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Current location: {activeSubmission.current_location}
                </p>
              )}
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Consent: publish {activeSubmission.consent_publish ? 'yes' : 'no'} · photo {activeSubmission.consent_photo ? 'yes' : 'no'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
